import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, Send, MessageSquare, Clock, CheckCircle2, PlusCircle, ArrowLeft } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = [
    { code: "technical", label: "Technical" },
    { code: "billing", label: "Billing" },
    { code: "content", label: "Content" },
    { code: "domain", label: "Domain" },
    { code: "other", label: "Other" },
];

function statusChip(s) {
    if (s === "open") return "ddi-chip ddi-chip-primary";
    if (s === "in_progress") return "ddi-chip ddi-chip-warning";
    if (s === "resolved") return "ddi-chip ddi-chip-success";
    return "ddi-chip ddi-chip-neutral";
}

export default function Support() {
    const [doctor, setDoctor] = useState(null);
    const [tickets, setTickets] = useState([]);
    const [selected, setSelected] = useState(null);
    const [thread, setThread] = useState(null);
    const [replyText, setReplyText] = useState("");
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ subject: "", category: "technical", message: "" });
    const [showNew, setShowNew] = useState(false);

    async function loadAll() {
        try {
            const [dr, tk] = await Promise.all([api.get("/me/doctor"), api.get("/me/tickets")]);
            setDoctor(dr.data);
            setTickets(tk.data || []);
        } catch (e) { toast.error(errMsg(e, "Failed")); }
    }
    useEffect(() => { loadAll(); }, []);

    async function openTicket(id) {
        setSelected(id);
        try {
            const r = await api.get(`/me/tickets/${id}`);
            setThread(r.data);
        } catch (e) { toast.error(errMsg(e, "Failed")); }
    }

    async function create() {
        if (!form.subject.trim() || !form.message.trim()) { toast.error("Subject and message are required"); return; }
        setCreating(true);
        try {
            const r = await api.post("/me/tickets", form);
            toast.success("Ticket created");
            setForm({ subject: "", category: "technical", message: "" });
            setShowNew(false);
            await loadAll();
            await openTicket(r.data.id);
        } catch (e) { toast.error(errMsg(e, "Failed")); }
        finally { setCreating(false); }
    }

    async function reply() {
        if (!replyText.trim() || !selected) return;
        try {
            await api.post(`/me/tickets/${selected}/reply`, { message: replyText });
            setReplyText("");
            await openTicket(selected);
            await loadAll();
        } catch (e) { toast.error(errMsg(e, "Failed")); }
    }

    const filtered = useMemo(() => tickets, [tickets]);

    return (
        <DoctorShell doctor={doctor}>
            <div className="mb-6 flex items-start justify-between flex-wrap gap-3" data-testid="support-header">
                <div>
                    <h1 className="ddi-section-title">Support</h1>
                    <p className="ddi-section-sub">Create tickets, chat with our team, and track the status of open issues.</p>
                </div>
                <Button onClick={() => { setSelected(null); setThread(null); setShowNew(true); }} className="bg-[#3A80F7] hover:bg-[#2D6BE0]" data-testid="support-new-btn">
                    <PlusCircle size={16} className="mr-2" /> New ticket
                </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* List */}
                <Card className="lg:col-span-5 border-[#E5E9F2] ddi-shadow-sm">
                    <CardHeader className="pb-3"><CardTitle className="text-base">Your tickets <span className="text-[#94A3B8] font-normal text-[13px]">· {tickets.length}</span></CardTitle></CardHeader>
                    <CardContent className="p-0">
                        {filtered.length === 0 ? (
                            <div className="p-6 text-center text-[13px] text-[#64748B]">
                                <LifeBuoy size={32} className="mx-auto text-[#94A3B8] mb-2" />
                                No tickets yet. Create your first one.
                            </div>
                        ) : (
                            <ul className="divide-y divide-[#EEF1F6] max-h-[560px] overflow-auto">
                                {filtered.map((t) => (
                                    <li key={t.id}>
                                        <button
                                            onClick={() => openTicket(t.id)}
                                            className={"w-full text-left px-4 py-3 hover:bg-[#F5F7FB] transition-colors " + (selected === t.id ? "bg-[#EEF4FE] border-l-4 border-[#3A80F7]" : "")}
                                            data-testid={`ticket-${t.id}`}
                                        >
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className={statusChip(t.status)}>{t.status.replace("_", " ")}</span>
                                                <span className="ddi-chip ddi-chip-neutral">{t.category}</span>
                                                {t.unread_by_doctor && <span className="w-2 h-2 rounded-full bg-[#3A80F7]" title="Unread reply" />}
                                            </div>
                                            <div className="text-[14px] font-semibold text-[#0F172A] truncate">{t.subject}</div>
                                            <div className="text-[11px] text-[#64748B] mt-0.5">
                                                {t.reply_count} {t.reply_count === 1 ? "reply" : "replies"} · Updated {new Date(t.last_activity_at).toLocaleString()}
                                            </div>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </CardContent>
                </Card>

                {/* Detail or Create */}
                <div className="lg:col-span-7">
                    {showNew ? (
                        <Card className="border-[#E5E9F2] ddi-shadow-sm">
                            <CardHeader className="pb-3 flex flex-row items-center justify-between">
                                <CardTitle className="text-base">New ticket</CardTitle>
                                <button onClick={() => setShowNew(false)} className="text-[#64748B] hover:text-[#0F172A] text-[13px] flex items-center gap-1"><ArrowLeft size={14} /> Cancel</button>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <label className="text-[11px] font-semibold uppercase tracking-widest text-[#64748B] mb-1.5 block">Category</label>
                                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                        <SelectTrigger data-testid="new-ticket-category"><SelectValue /></SelectTrigger>
                                        <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label className="text-[11px] font-semibold uppercase tracking-widest text-[#64748B] mb-1.5 block">Subject</label>
                                    <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Short summary" maxLength={200} data-testid="new-ticket-subject" />
                                </div>
                                <div>
                                    <label className="text-[11px] font-semibold uppercase tracking-widest text-[#64748B] mb-1.5 block">Message</label>
                                    <Textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Describe the issue with as much detail as possible." maxLength={5000} data-testid="new-ticket-message" />
                                </div>
                                <Button onClick={create} disabled={creating} className="bg-[#3A80F7] hover:bg-[#2D6BE0]" data-testid="new-ticket-submit">
                                    {creating ? "Creating…" : "Create ticket"}
                                </Button>
                            </CardContent>
                        </Card>
                    ) : thread ? (
                        <Card className="border-[#E5E9F2] ddi-shadow-sm">
                            <CardHeader className="pb-3">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className={statusChip(thread.ticket.status)}>{thread.ticket.status.replace("_", " ")}</span>
                                    <span className="ddi-chip ddi-chip-neutral">{thread.ticket.category}</span>
                                </div>
                                <CardTitle className="text-base">{thread.ticket.subject}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3 max-h-[420px] overflow-y-auto pr-2" data-testid="ticket-thread">
                                    {(thread.messages || []).map((m) => {
                                        const isDoctor = m.author_role === "doctor";
                                        return (
                                            <div key={m.id} className={"rounded-xl p-3 border " + (isDoctor ? "bg-[#EEF4FE] border-[#3A80F7]/20 ml-6" : "bg-white border-[#E5E9F2] mr-6")}>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className={"text-[11px] font-semibold " + (isDoctor ? "text-[#3A80F7]" : "text-[#64748B]")}>
                                                        {isDoctor ? "You" : "Support"}
                                                    </span>
                                                    <span className="text-[10px] text-[#94A3B8]">{new Date(m.created_at).toLocaleString()}</span>
                                                </div>
                                                <div className="text-[13px] text-[#0F172A] whitespace-pre-line">{m.message}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {thread.ticket.status !== "closed" ? (
                                    <div className="mt-4 pt-4 border-t border-[#E5E9F2]">
                                        <Textarea rows={3} value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Type your reply…" maxLength={5000} data-testid="ticket-reply-input" />
                                        <div className="mt-3 flex justify-end">
                                            <Button onClick={reply} disabled={!replyText.trim()} className="bg-[#3A80F7] hover:bg-[#2D6BE0]" data-testid="ticket-reply-send">
                                                <Send size={14} className="mr-2" /> Send reply
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="mt-4 pt-4 border-t border-[#E5E9F2] text-[12px] text-[#94A3B8] text-center">This ticket is closed.</div>
                                )}
                            </CardContent>
                        </Card>
                    ) : (
                        <Card className="border-[#E5E9F2] border-dashed">
                            <CardContent className="py-16 text-center">
                                <MessageSquare size={40} className="mx-auto text-[#94A3B8] mb-3" />
                                <div className="text-[15px] font-semibold text-[#0F172A]">Select a ticket to view</div>
                                <div className="text-[13px] text-[#64748B] mt-1">Or click "New ticket" to create one.</div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>
        </DoctorShell>
    );
}
